/**
 * Avatar API service for uploading and retrieving user avatars.
 */

import { BaseApiService } from './BaseApiService';
import { API_URL } from '@/lib/apiConfig';

export interface AvatarUploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface AvatarConfirmResponse {
  avatarUrl: string;
  s3Key: string;
  success: boolean;
}

export interface AvatarResponse {
  avatarUrl: string | null;
  exists: boolean;
  s3Key?: string;
}

class AvatarApiService extends BaseApiService {
  constructor() {
    super(API_URL, 'AvatarApi');
  }

  /**
   * Get the current user's avatar URL.
   */
  async getAvatar(): Promise<AvatarResponse> {
    this.log.debug('Getting avatar', { action: 'getAvatar' });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/user/avatar`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get avatar', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get avatar');
    }

    const data = await response.json();
    this.log.info('Avatar retrieved', { action: 'getAvatar', exists: data.exists });
    return data;
  }

  /**
   * Get a presigned URL for uploading an avatar.
   */
  async getUploadUrl(filename: string, contentType: string): Promise<AvatarUploadUrlResponse> {
    this.log.debug('Getting avatar upload URL', { action: 'getUploadUrl', filename, contentType });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/user/avatar/upload-url`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ filename, contentType }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to get upload URL', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to get upload URL');
    }

    const data = await response.json();
    return data;
  }

  /**
   * Upload an avatar image to S3 using a presigned URL.
   */
  async uploadAvatar(
    presignedUrl: string,
    file: Blob,
    contentType: string,
    onProgress?: (percent: number) => void
  ): Promise<void> {
    this.log.debug('Uploading avatar', { action: 'uploadAvatar', contentType, size: file.size });

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          this.log.info('Avatar uploaded successfully', { action: 'uploadAvatar' });
          resolve();
        } else {
          this.log.error('Avatar upload failed', new Error(`HTTP ${xhr.status}`));
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        this.log.error('Avatar upload network error', new Error('Network error'));
        reject(new Error('Network error during upload'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.send(file);
    });
  }

  /**
   * Confirm avatar upload and save S3 key to Cognito.
   */
  async confirmUpload(s3Key: string): Promise<AvatarConfirmResponse> {
    this.log.debug('Confirming avatar upload', { action: 'confirmUpload', s3Key });
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/user/avatar/confirm`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ s3Key }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      this.log.error('Failed to confirm upload', new Error(data.error || `HTTP ${response.status}`));
      throw new Error(data.error || 'Failed to confirm upload');
    }

    const data = await response.json();
    this.log.info('Avatar upload confirmed', { action: 'confirmUpload', s3Key });
    return data;
  }
}

// Export singleton instance
export const avatarApi = new AvatarApiService();
